import axios from "axios";
import { EnhancedStore } from "@reduxjs/toolkit";
import { toast } from "sonner";
import { isSandboxMode, mintSandboxSession } from "./sandbox";

// Prefer configured API base URL, else fall back to same-origin
const configuredApi = import.meta.env.VITE_API_BASE_URL as string | undefined;
const resolvedBase =
  configuredApi && configuredApi.trim()
    ? configuredApi
    : typeof window !== "undefined"
      ? window.location.origin
      : "";

export const apiClient = axios.create({
  baseURL: `${resolvedBase}/v2`,
  withCredentials: true,
});

if (!configuredApi) {
  // eslint-disable-next-line no-console
  console.warn(
    "VITE_API_BASE_URL is not set. Falling back to same-origin:",
    `${resolvedBase}/v2`,
  );
}

export const setupInterceptors = (store: EnhancedStore) => {
  apiClient.interceptors.request.use(
    (config) => {
      const token = store.getState().appAuthReducer.appToken;
      try {
        if (token) {
          // Lightweight marker without dumping token
          console.debug(
            "[auth][request]",
            config.method,
            config.url,
            "authAttached=",
            !!token,
          );
        }
      } catch {}
      const alreadySet = (config.headers as any)?.Authorization;
      if (token && !alreadySet) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Sandbox sessions: when the short-lived demo token expires mid-session,
  // transparently mint a fresh anonymous one and retry the request once.
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error?.config;
      if (
        isSandboxMode() &&
        error?.response?.status === 401 &&
        original &&
        !original.__sandboxRetried
      ) {
        try {
          const token = await mintSandboxSession();
          // Plain action object to avoid an import cycle with appAuthSlice.
          store.dispatch({ type: "appAuth/setAppToken", payload: token });
          original.__sandboxRetried = true;
          original.headers = {
            ...original.headers,
            Authorization: `Bearer ${token}`,
          };
          return apiClient(original);
        } catch {
          // Fall through to the original error (e.g. sandbox disabled).
        }
      }
      return Promise.reject(error);
    },
  );

  // Plan gating: the API rejects features the org's plan does not include.
  // Surface it explicitly so a stale UI (e.g. a downgrade in another tab)
  // never fails with an opaque error.
  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      const data = error?.response?.data;
      if (
        error?.response?.status === 403 &&
        data?.code === "PLAN_UPGRADE_REQUIRED"
      ) {
        toast.error("Upgrade required", {
          description: `This feature is available from the ${
            data.requiredPlan || "Starter"
          } plan.`,
        });
      }
      return Promise.reject(error);
    },
  );
};
